// Billing (docs/superpowers/plans/2026-10-19-gitea-github-journey.md, group 7):
// an organisation pays through Mollie. "Start paying" opens Mollie's checkout for
// a first payment; once it is paid, Mollie holds a mandate for later charges and
// the organisation's plan becomes `paid`.
import { z } from "zod";
import { countryCode } from "../countries";
import { EU_COUNTRIES } from "../pricing";

/** Self-service billing is for organisations in the EU; anyone else talks to Convenor first. */
export const OUTSIDE_EU_MESSAGE = "For an address outside the EU, talk to us.";

/** The organisation's billing state. `none`: never started; `pending`: a checkout is open. */
export const BILLING_STATUSES = ["none", "pending", "active", "failed"] as const;
export type BillingStatus = (typeof BILLING_STATUSES)[number];

/** Mollie's payment statuses (docs.mollie.com/reference/create-payment). */
export const MOLLIE_PAYMENT_STATUSES = ["open", "pending", "authorized", "paid", "canceled", "expired", "failed"] as const;
export type MolliePaymentStatus = (typeof MOLLIE_PAYMENT_STATUSES)[number];

export type BillingPayment = {
  id: string;
  description: string;
  amountCents: number;
  currency: string;
  status: MolliePaymentStatus;
  createdAt: string;
  paidAt: string | null;
  /** What it was for: `start` is the €0.01 check of payment details, the rest are charges. */
  purpose: BillingPurpose;
};

export type BillingOverview = {
  status: BillingStatus;
  /** True while Convenor talks to Mollie's test mode: no real money moves. */
  testMode: boolean;
  /** False when the server has no Mollie key: the page explains instead of offering a button. */
  available: boolean;
  payments: BillingPayment[];
  /** null until an owner or admin saved them (POST /billing/start needs them). */
  details: BillingDetails | null;
  /** The end of the year paid for (or being charged); null during the trial. */
  paidUntil: string | null;
  /** What the mandate is charged next, counting today's projects; null without a mandate, when cancelled, or while a charge failed. */
  nextCharge: { on: string; grossCents: number; projectCount: number; vatCents: number; reverseCharge: boolean } | null;
};

/** returnTo: where Mollie sends the payer back (the settings page, or "Add payment details" in the sign-up). */
export const startPayingBody = z.object({ returnTo: z.enum(["settings", "welcome"]).default("settings") }).strict();

export type StartPayingResult = { checkoutUrl: string };

/**
 * Why a payment was made (stages 2 and 3): `start` the first payment that sets up the mandate
 * (at the start of the trial); `first_year` charged on day 30; `added_project` the share of the
 * year for a project added later; `renewal` each following year.
 */
export const BILLING_PURPOSES = ["start", "first_year", "added_project", "renewal"] as const;
export type BillingPurpose = (typeof BILLING_PURPOSES)[number];

/** The buyer as the invoice shows them, copied from the organisation's billing details when it was issued. */
export type InvoiceBuyer = { name: string; address: string; country: string; email: string; vatNumber: string | null };

/** One line on an invoice; amounts in cents, excluding VAT. */
export type InvoiceLine = { description: string; quantity: number; unitCents: number; amountCents: number };

/** "de 123.456.789" -> "DE123456789": the form VIES and the invoice use. */
export const normaliseVatNumber = (v: string): string => v.replace(/[\s.-]/g, "").toUpperCase();

/** Billing details (stage 2.1): asked before "Continue to Mollie", printed on every invoice. */
export const billingDetailsBody = z
  .object({
    name: z.string().trim().min(1, "Enter the name to put on the invoice.").max(200),
    address: z.string().trim().min(1, "Enter the address to put on the invoice.").max(1000),
    country: countryCode.refine((c) => EU_COUNTRIES.includes(c), OUTSIDE_EU_MESSAGE),
    email: z.string().trim().toLowerCase().email("Enter an email address like name@example.org.").max(254),
    vatNumber: z
      .string()
      .max(30)
      .transform(normaliseVatNumber)
      .refine((v) => v === "" || /^[A-Z0-9]{4,20}$/.test(v), "Enter the VAT number as it is registered, for example DE123456789.")
      .transform((v) => (v === "" ? null : v))
      .nullish(),
  })
  .strict();
export type BillingDetailsBody = z.input<typeof billingDetailsBody>;

export type BillingDetails = {
  name: string;
  address: string;
  country: string;
  email: string;
  vatNumber: string | null;
  /** What EU VIES said; null when not checked (no number, the Netherlands, or outside the EU). */
  vatValid: boolean | null;
};

/** An invoice in the billing screen's list; the PDF is GET /api/billing/invoices/:id/pdf. */
export type BillingInvoiceSummary = { id: string; number: string; issuedAt: string; netCents: number; vatCents: number; grossCents: number; description: string };
export type BillingInvoicesResponse = { invoices: BillingInvoiceSummary[] };
